import { createVoiceTranscript } from './voiceTranscript';

// One voice conversation, as protocol and state only: what to say to the relay, and when.
//
// The audio ports and the socket are injected rather than built here, so this — the part with the
// rules in it — is reachable from a test, while Web Audio and the WebSocket stay in thin adapters
// that are not. The same move VoiceRelaySession makes on the server.

// How long after the button comes up the turn stays open for audio still on its way.
const FLUSH_TAIL_MS = 300;

// The one measure of whether a press was a real attempt to speak. Under it, nothing happened: the
// buffer is thrown away rather than offered as a turn, and — since a cancelled answer cannot be
// brought back — the AI is never interrupted by a hand brushing the button. Over it, the press both
// interrupts and commits, so the AI talks across the beginning of the user by exactly this long.
//
// A quarter of a second by default, which with the tail is enough to have said a short word. It is
// the caller's to change: how long a hand takes to mean something is not a fact about the protocol.
const MINIMUM_HOLD_MS = 250;

// A chunk of the model's speech, under the name the API gives it now and the one it gave before.
const AUDIO_DELTAS = new Set(['response.output_audio.delta', 'response.audio.delta']);

// The same chunk in words. Both spellings again, and for the same reason.
const TRANSCRIPT_DELTAS = new Set([
  'response.output_audio_transcript.delta',
  'response.audio_transcript.delta',
]);

// How often the record is re-measured against the speakers. Fast enough that the words keep up
// with the voice, slow enough that a conversation is not a hundred renders a second.
const REVEAL_MS = 100;

// What the student turned out to have said, and the admission that it will never be known.
const TRANSCRIBED = 'conversation.item.input_audio_transcription.completed';
const TRANSCRIPTION_FAILED = 'conversation.item.input_audio_transcription.failed';

// The server found the committed buffer empty. One of push-to-talk's own races, and the only
// error that says anything about a turn: the student held the button and nothing came of it.
const COMMIT_EMPTY = 'input_audio_buffer_commit_empty';

// Every way an answer stops being in progress. Waiting only for response.done leaves the session
// convinced one is still running for as long as it lives, and it never asks for another.
const RESPONSE_ENDED = new Set([
  'response.done',
  'response.cancelled',
  'response.incomplete',
  'response.failed',
]);

// The one ending nobody needs telling about. Note what it is not: a socket this end closes carries
// no status at all, which arrives back as 1005, and a connection that dies without a close frame is
// reported as 1006. Neither is this, and only one of them is worth a sentence.
const CLEAN_CLOSE = 1000;

// What the relay closes with, and what each one means to the person who was talking. Mirrors the
// table VoiceSessionOutcome publishes on the server — the numbers are 4000 plus the HTTP status the
// same refusal carries, so 4402 is the 402 an ordinary AI call is refused with.
const CLOSE_REASONS = {
  4401: 'ticketRefused',
  4402: 'allowanceExhausted',
  4502: 'upstreamLost',
  4503: 'upstreamUnavailable',
};

// Which way the microphone refused. Only these two are worth telling apart: one is a permission the
// user can grant, the other is a device they have to go and find. Everything else — a microphone
// another application holds, constraints nothing satisfies, or a plain-http origin where
// navigator.mediaDevices is undefined and the failure is a TypeError rather than any DOMException —
// is the same shrug, and pretending otherwise would send people to settings that are already right.
const MICROPHONE_REASONS = {
  NotAllowedError: 'microphoneDenied',
  NotFoundError: 'microphoneMissing',
};

export function createVoiceSession({
  openSocket,
  audio,
  onState = () => {},
  onTurns = () => {},
  onScene = () => {},
  // Whether the AI is talking at this moment, told to the screen on every tick of the reveal rather
  // than when it changes: a screen waiting for the room to go quiet may start waiting when it
  // already is, and an edge it missed would never come round again.
  onSpeaking = () => {},
  onFailure = () => {},
  // Whether the relay greets this session of its own accord. It greets characters; a teacher
  // opens silent, and a silent session has no greeting to keep out of the way of.
  greeted = true,
  // Whether this session opens the microphone. A listen-only session — something announced at
  // whoever happens to be standing there — has nobody to talk to, so it never asks for the device:
  // a permission prompt for a microphone that will never send a frame is one the student can only
  // get wrong, and a granted one lights the recording indicator over a session that only listens.
  microphone = true,
  minimumHoldMs = MINIMUM_HOLD_MS,
}) {
  let socket = null;
  let talking = false;
  let pressedAt = 0;
  let responseActive = false;
  let turnOwed = false;
  let flushTimer = null;
  let bargeTimer = null;
  let stopped = false;
  // Which item the answer being played belongs to, and where in it. Null once there is nothing left
  // to cut short.
  let spoken = null;
  // Set when a running answer is cut off, cleared by that answer's own ending. What the provider
  // had already put on the wire keeps arriving after the cancel, and playing it would start the AI
  // talking again out of a queue that was just emptied.
  //
  // The ending is the all-clear rather than the next answer's beginning, for two reasons. An answer
  // asked for optimistically can have its response.created still in flight when it is cut off, so
  // that event belongs to the answer being silenced and would un-gag it. And every answer ends
  // somehow, while an answer that is never created would leave this set for the rest of the
  // session, and silence is a poor terminal state.
  let muted = false;
  // Whether this turn has already destroyed an answer. A press can stop the AI and then, taken up
  // again as a stutter, end too short to be a turn: the answer is gone and nothing replaces it.
  let interrupted = false;
  // Whether this hold has already had its interruption. The timer is one way it happens; the
  // release is the other, and neither may do it twice.
  let barged = false;
  let revealTimer = null;
  // What the screen last saw, so a tick that changes nothing does not re-render the conversation.
  let published = '';
  const transcript = createVoiceTranscript();

  // The record is re-measured on a timer rather than on each chunk: the words are paced by what
  // the speakers have already said, and that goes on advancing between events.
  function publish() {
    const turns = transcript.turns();
    const state = JSON.stringify(turns);
    if (state !== published) {
      published = state;
      onTurns(turns);
    }
  }

  function reveal() {
    transcript.playedTo(audio.playedMs());
    publish();
    onSpeaking(audio.speaking());
  }

  function send(event) {
    socket.send(JSON.stringify(event));
  }

  // An answer is in progress from the moment it is asked for, not from the moment the server says
  // so: believing otherwise across that round trip asks twice, which the API refuses.
  function ask() {
    turnOwed = false;
    responseActive = true;
    send({ type: 'response.create' });
  }

  // A turn committed while an answer was running is answered at the first moment it can be, and
  // never while the button is down. An answer begun into a hold is one that hold is about to cut
  // off — generated, billed and cancelled a moment later, over a student still speaking.
  function payDebt() {
    if (turnOwed && !talking && !responseActive) {
      ask();
    }
  }

  // A press held long enough to be a turn is a deliberate interruption. What is queued to play is
  // dropped, what has not been generated is cancelled, and the item is cut back to what actually
  // reached the room: the model would otherwise build on sentences the student never heard.
  //
  // Only the cancel is conditional. The provider generates faster than it speaks, so the answer is
  // usually complete, and often already reported done, while the browser is still playing it —
  // which is the ordinary shape of an interruption, not an edge of it.
  function bargeIn() {
    barged = true;
    // `cut` is what the speakers still had left to say. Without it a press that begins after the
    // last word would tell the model to trim an answer it had finished delivering — or, if nothing
    // in between was ever spoken aloud, to trim an older item that has nothing to do with this.
    const { heardMs, cut } = audio.flush();
    // Struck under the same condition the truncation below is sent under, and for the same reason:
    // only an answer that still had something to say has anything to lose. One already heard to
    // its end would otherwise be redisplayed as words the student missed while they sat through
    // them — and, no truncation having been sent, the record would say so while the model does not.
    if (cut) {
      const skipped = transcript.interrupted(heardMs);
      publish();
      // Autolang's own frame, not the provider's: only this end owns the playback clock, and the
      // record kept on the server is cut at the same character the screen strikes through. The
      // relay consumes it and never forwards it.
      send({ type: 'autolang.interrupted', skipped });
    }
    // Nothing to be deaf to unless an answer is still running: one that has already ended has
    // delivered everything it ever will.
    muted = responseActive;
    interrupted = interrupted || cut || responseActive;

    if (responseActive) {
      send({ type: 'response.cancel' });
    }

    if (cut && spoken) {
      send({
        type: 'conversation.item.truncate',
        item_id: spoken.itemId,
        content_index: spoken.contentIndex,
        // Rounded down: a millisecond past the end of the audio is an error, not a longer memory.
        audio_end_ms: Math.floor(heardMs),
      });
      spoken = null;
    }
  }

  // Frames arrive from the audio thread whether or not a turn is open: the microphone is opened
  // once and left open. Discarding them here is what makes a turn, and only a turn, cost anything
  // — including a press the browser never gave us a release for.
  function heard(base64) {
    if (talking) {
      send({ type: 'input_audio_buffer.append', audio: base64 });
    }
  }

  function pressTalk() {
    // Button up and straight back down is a stutter, not two turns: the hand-over already
    // scheduled would otherwise commit half a sentence, or clear what this hold has just said.
    clearTimeout(flushTimer);
    clearTimeout(bargeTimer);
    talking = true;
    pressedAt = Date.now();
    barged = false;
    audio.setCapturing(true);
    // Armed, not fired: the AI is interrupted because the press lasted, not because it began.
    bargeTimer = setTimeout(bargeIn, minimumHoldMs);
  }

  function releaseTalk() {
    const held = Date.now() - pressedAt;
    // Called off on the release itself rather than at the end of the tail: a button let go just
    // under the threshold must not go on to interrupt anything just over it.
    clearTimeout(bargeTimer);
    // The timer reads the event loop and this reads the clock, and a busy thread delays one
    // without delaying the other. A hold long enough to be a turn is long enough to interrupt,
    // whichever of the two noticed first.
    if (held >= minimumHoldMs && !barged) {
      bargeIn();
    }
    // Capture stays open across the tail. Closing it on the release itself would drop the frames
    // still crossing the message port — the very syllables the wait exists to keep.
    flushTimer = setTimeout(() => {
      talking = false;
      audio.setCapturing(false);
      const stoppedAnswer = interrupted;
      interrupted = false;

      if (held < minimumHoldMs) {
        send({ type: 'input_audio_buffer.clear' });
        // Discarding audio that had already stopped the AI would leave the conversation empty:
        // the answer is gone and this press said nothing to replace it. Owe one instead.
        turnOwed = turnOwed || stoppedAnswer;
        payDebt();
        return;
      }

      send({ type: 'input_audio_buffer.commit' });
      // The bubble is made here, not when the words come back: transcription finishes after the
      // turn is over, and a record that waited for it would show the answer above the question.
      transcript.committed();
      publish();
      // Committing puts the turn in the conversation whatever else is happening. Asking for an
      // answer cannot: while one is running the ask is owed, and paid when that one ends.
      if (responseActive) {
        turnOwed = true;
      } else {
        ask();
      }
    }, FLUSH_TAIL_MS);
  }

  // The backend disables server turn detection with a session.update as the relay opens, and
  // session.updated is its acknowledgement. Nothing may happen before it: until then the server is
  // still detecting turns of its own, and this session commits turns itself.
  function receive(event) {
    // The browser keeps dispatching what the socket had already queued after it is closed; acting
    // on it means playing into an AudioContext that has been torn down.
    if (stopped) {
      return;
    }

    // Autolang's own frame coming the other way: what the character just did to the scene, as the
    // server sees it. Not a session event, so it stops here rather than falling through the chain
    // below — and it is never echoed back up, where the relay's allowlist would drop it anyway.
    if (event.type?.startsWith('autolang.')) {
      onScene(event);
      return;
    }

    if (event.type === 'session.updated') {
      onState('ready');
      // The relay asked for a greeting right behind its session.update, so an answer is already
      // in progress by the time this acknowledgement arrives — asking again would collide with
      // it. Not so for a session opened silent: believing in an answer that was never asked for
      // would cancel it on the first hold and leave the first turn owed to it forever.
      responseActive = greeted;
    } else if (AUDIO_DELTAS.has(event.type)) {
      // The socket keeps its order, so everything belonging to an answer that was cut off arrives
      // before that answer ends: one flag is as exact here as matching the response id, and owes
      // nothing to a field name the API has already changed once.
      if (!muted) {
        // Where one answer ends and the next begins is known here, off the item every delta
        // carries. The speakers measure from an answer's first chunk, so they are told rather
        // than left to work it out a second time from the same field.
        if (event.item_id !== spoken?.itemId) {
          audio.startAnswer();
        }
        spoken = { itemId: event.item_id, contentIndex: event.content_index ?? 0 };
        // Booking the chunk is what dates the words: everything written by now is carried by
        // audio ending here, and will be shown when the speakers reach it.
        transcript.booked(audio.play(event.delta));
        publish();
      }
    } else if (TRANSCRIPT_DELTAS.has(event.type)) {
      // Not gated on `muted`, unlike the audio. What the provider goes on writing after a cancel
      // is what the student cut off, and the record shows it struck through rather than losing it.
      transcript.spoke(event.delta);
      publish();
    } else if (event.type === 'input_audio_buffer.committed') {
      // The server names the turn it has just taken. Until now the bubble had nothing a
      // transcription could be matched against.
      transcript.bound(event.item_id);
    } else if (event.type === TRANSCRIBED) {
      transcript.transcribed(event.item_id, event.transcript ?? '');
      publish();
    } else if (event.type === TRANSCRIPTION_FAILED) {
      transcript.failed(event.item_id);
      publish();
    } else if (event.type === 'error' && event.error?.code === COMMIT_EMPTY) {
      transcript.commitRejected();
      publish();
    } else if (event.type === 'error') {
      // An error can be the only trace of an answer the server refused: a response.create
      // rejected leaves nothing response.* to follow it. Left in progress, that answer would
      // gate every turn for the rest of the session.
      responseActive = false;
      payDebt();
    } else if (event.type === 'response.created') {
      responseActive = true;
      transcript.answerStarted();
      publish();
    } else if (RESPONSE_ENDED.has(event.type)) {
      responseActive = false;
      muted = false;
      transcript.answerEnded();
      publish();
      payDebt();
    }
  }

  // Both ways a session ends meet here: the user pressing Stop, and the relay hanging up by
  // itself. Whichever comes first is the one that counts — the other finds the work already done.
  function stop() {
    if (stopped) {
      return;
    }
    stopped = true;
    // The tail is still pending when a session is stopped mid-hold; letting it run would commit a
    // turn to a socket nobody is listening to, as would interrupting an answer nobody can hear.
    clearTimeout(flushTimer);
    clearTimeout(bargeTimer);
    clearInterval(revealTimer);
    talking = false;
    // There may be no socket yet: the user can give up while the microphone is still being asked
    // for, and the microphone still has to be let go of.
    socket?.close();
    audio.close();
    // Nothing more will be said, whatever was still booked when this happened. A screen holding
    // something back until the AI stopped talking would otherwise hold it for the rest of its life.
    onSpeaking(false);
    onState('stopped');
  }

  // The relay hanging up, for any reason including none. The session ends either way; what differs
  // is whether the user is owed an explanation for it.
  function closed(event) {
    // Read before stopping, since stopping is what sets it: a close that arrives because the user
    // pressed Stop is the ending they asked for, whatever code the browser attaches to it.
    const asked = stopped;
    stop();
    if (asked) {
      return;
    }

    const code = event?.code;
    if (code === undefined || code === CLEAN_CLOSE) {
      return;
    }
    onFailure(CLOSE_REASONS[code] ?? 'endedUnexpectedly');
  }

  async function start() {
    onState('connecting');
    transcript.cleared();
    publish();
    // The microphone first, and the socket — with the short-lived ticket that opens it — only
    // once it is granted. A permission prompt outlives a ticket easily.
    //
    // Reported rather than thrown, so that a microphone that would not open and a relay that hung
    // up arrive at the same handler: the screen has one thing to say about a session that failed,
    // and no reason to care which half of it did.
    try {
      // No ear for a listen-only session, which is the whole of how its microphone stays shut at
      // this end: the speakers are opened either way, since an announcement is there to be heard.
      await audio.open(microphone ? heard : null);
    } catch (error) {
      stop();
      onFailure(MICROPHONE_REASONS[error?.name] ?? 'microphoneUnavailable');
      return;
    }
    if (stopped) {
      return;
    }

    socket = await openSocket();
    // Stopped while it was opening: the socket has arrived for a session nobody is in any more.
    // stop() ran when there was nothing yet to close, so closing it is this branch's to do — and
    // an unclosed relay leg goes on costing money long after the screen has been left.
    if (stopped) {
      socket.close();
      return;
    }

    socket.onmessage = (message) => receive(JSON.parse(message.data));
    socket.onclose = closed;
    revealTimer = setInterval(reveal, REVEAL_MS);
  }

  return { start, pressTalk, releaseTalk, stop };
}
